import { useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { initializeChakraComponent } from 'Shared/chakra-initialize';
import { CharacterCount } from './components/CharacterCount';
import { EditorContent, useEditor } from '@tiptap/react';
import { SubmitErrorComponent } from './components/SubmitError';
import type { EditorPreset } from './presets';
import { useEditorContext, useEditorSelector, EditorContext } from './editor_context';
import { getEditor, removeEditor, setEditor } from './editor_cache';
import {
   parseEditorContent,
   getDefaultExtensions,
   getExtensionsForPreset,
   MenuBar,
} from './presets';
import type { CharacterCountConfig } from './extensions/custom_character_count';

/**
 * An unnamed editor still needs an editorProps object, not undefined: Editor
 * merges the options it's handed over its defaults, so an explicit undefined
 * clobbers Tiptap's own `{}` and createView() then reads
 * `editorProps.dispatchTransaction` off it unguarded. Shared, so the identity
 * stays stable across renders (see the useMemo below).
 */
const NO_EDITOR_PROPS = {};

const TiptapEditorComponent = (props: {
   editorid: string;
   preset: EditorPreset;
   characterCountConfig?: CharacterCountConfig;
   disabled?: boolean;
   onEditorReady?: () => void;
   /** Rendered right-aligned inside the menubar (guide_intro and wiki presets). */
   menubarEnd?: ReactNode;
}) => {
   const { editorid, characterCountConfig, preset, disabled, onEditorReady, menubarEnd } = props;
   const { editor } = useEditorContext();
   // Both are extension storage, not ProseMirror state, but every write to
   // them rides a transaction (focus/blur dispatch one in core; showError and
   // clearError run through the command manager, which always dispatches), so
   // the transaction subscription sees them.
   const storageState = useEditorSelector(editor => ({
      isFocused: editor.storage.isFocused?.isFocused ?? false,
      submitError: editor.storage.submitError?.error ?? null,
   }));

   useEffect(() => {
      if (!editor || editor.isDestroyed) {
         return;
      }

      if (disabled) {
         editor.setEditable(false);
      }
   }, [editor, disabled]);

   useEffect(() => {
      if (!editor || editor.isDestroyed || !onEditorReady) {
         return;
      }

      onEditorReady();
   }, [editor, onEditorReady]);

   // Tiptap v3 nulls commandManager on destroy; toolbar selectors call
   // editor.can() during render, so skip rendering while tearing down.
   if (!editor || editor.isDestroyed || !storageState) {
      return null;
   }

   const { isFocused, submitError } = storageState;

   return (
      <>
         <div id={editorid} className="ProseMirror-menubar-wrapper">
            {!disabled && <MenuBar preset={preset} menubarEnd={menubarEnd} />}
            <EditorContent editor={editor} />
         </div>
         {isFocused && characterCountConfig ? <CharacterCount /> : null}
         {submitError ? <SubmitErrorComponent error={submitError} /> : null}
         {disabled && (
            <style>
               {`
               .ProseMirror-menubar-wrapper .ProseMirror {
                  background-color: #d3d7d9;
                  border: 1px solid rgba(0, 3, 6, 0.12);
                  cursor: not-allowed;
                  margin-top: 15px;
                  opacity: 0.6;
                  pointer-events: none;
               }
            `}
            </style>
         )}
      </>
   );
};

export type TiptapEditorProps = Parameters<typeof TiptapEditorComponent>[0] & {
   text?: string;
   submitHandler?: () => void;
   handleModified?: (modified: boolean) => void;
   /** Names the contenteditable for screen readers; mirror the field's visible label. */
   ariaLabel?: string;
   /**
    * Points the contenteditable's accessible name at an external
    * <label>. `editorid` only lands on the menubar wrapper div (see
    * TiptapEditorComponent above), and ARIA name computation doesn't
    * inherit an ancestor's aria-labelledby, so a caller relying on that
    * wrapper — or on its own wrapping element — to name the field
    * leaves the actual textbox (ProseMirror's contenteditable) unnamed.
    */
   ariaLabelledBy?: string;
};

export const TiptapEditor = (props: TiptapEditorProps) => {
   const { editorid, text = '', preset, ariaLabel, ariaLabelledBy } = props;

   // getDefaultExtensions() calls .configure(), minting fresh extension
   // instances that defeat useEditor's element-wise options comparison and
   // trigger a spurious editor.setOptions() per render. An empty dep list is
   // safe: Editor builds its extension manager in the constructor and
   // setOptions never rebuilds it, so only the first array is load-bearing.
   const extensions = useMemo(
      () => [...getDefaultExtensions(props), ...getExtensionsForPreset(preset)],
      // eslint-disable-next-line react-hooks/exhaustive-deps -- built once, at editor creation
      []
   );

   // useEditor compares options with !== (everything but extensions and the
   // lifecycle callbacks), so a fresh object here would call setOptions —
   // and through it view.updateState() — on every render.
   const editorProps = useMemo(() => {
      if (!ariaLabel && !ariaLabelledBy) {
         return NO_EDITOR_PROPS;
      }
      return {
         // Tiptap only merges its own role="textbox" in when it builds the
         // view; a later setOptions() replaces `attributes` wholesale, so
         // carry it here.
         attributes: {
            'role': 'textbox',
            ...(ariaLabelledBy && { 'aria-labelledby': ariaLabelledBy }),
            ...(ariaLabel && { 'aria-label': ariaLabel }),
         },
      };
   }, [ariaLabel, ariaLabelledBy]);

   const editor = useEditor({
      extensions,
      content: parseEditorContent(text),
      // Toolbar state subscribes through useEditorSelector; the legacy
      // whole-tree re-render per transaction is off.
      shouldRerenderOnTransaction: false,
      editorProps,
   });

   const editorContext = useMemo(() => ({ editor, preset }), [editor, preset]);

   useEffect(() => {
      if (editor) {
         setEditor(editorid, editor);
      }
      return () => {
         // Purge on unmount: cache consumers (focusEditor, resetContent, …)
         // must not operate on a destroyed editor. Only remove our own entry —
         // a replacement editor may already have been cached.
         if (getEditor(editorid) === editor) {
            removeEditor(editorid);
         }
      };
   }, [editor, editorid]);

   return (
      <EditorContext.Provider value={editorContext}>
         <TiptapEditorComponent {...props} />
      </EditorContext.Provider>
   );
};

initializeChakraComponent('TiptapEditor', TiptapEditor);
